import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useCapabilities } from '../../lib/capabilities'
import { formatDate, formatNumber, titleCase } from '../../lib/format'
import type { Sale } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select,
  Skeleton,
} from '../../components/ui'
import { Wizard } from '../../components/Wizard'
import { CloseIcon, CoinIcon, PlusIcon } from '../../components/icons'

interface SalesResponse {
  ok: boolean; items: Sale[]; total: number
  minerals: [string, string][]; units: string[]; sites: { id: string; name?: string }[]
}

export default function Sales() {
  /* Capture vs reporting separation: anyone holding sales.log may RECORD a
   * sale, but the organisation-wide sales history below is reporting, and
   * only shows for roles that hold a reports capability - a pure capturer
   * sees the capture wizard and nothing else. */
  const caps = useCapabilities()
  const canViewReports = caps.has('*') || caps.has('reports.view.sensitive') || caps.has('reports.view.operations')
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops', 'finance', 'sales'],
    queryFn: () => api.get<SalesResponse>('/ops/finance/sales'),
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.offline.post('/ops/finance/sales', body, 'Sale'),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['ops', 'finance', 'sales'] }); setShowForm(false) },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    create.mutate({
      mineral: form.get('mineral'),
      value: Number(form.get('value') ?? 0),
      quantity: form.get('quantity') ? Number(form.get('quantity')) : undefined,
      unit: form.get('unit'),
      buyer: form.get('buyer'),
      site_id: form.get('site_id'),
      copper_price: form.get('copper_price') ? Number(form.get('copper_price')) : undefined,
      sold_at: form.get('sold_at'),
      notes: form.get('notes'),
    })
  }

  const items = data?.items ?? []
  const minerals = data?.minerals ?? []
  const units = data?.units?.length ? data.units : ['g', 'kg', 'tonnes', 'carats', 'units']
  const sites = data?.sites ?? []
  const isCopper = (v: string) => v === 'copper'

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Mineral sales</h1>
          <p className="page-head__sub">Record revenue — feeds royalty and P&amp;L calculations.</p>
        </div>
        <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : 'Record sale'}
        </Button>
        <button
          type="button" className="fab"
          onClick={() => setShowForm((s) => !s)}
          aria-label={showForm ? 'Cancel' : 'Record sale'}
        >
          {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
        </button>
      </div>

      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Record a sale" />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack">
                {create.isError && (
                  <Alert tone="danger">
                    {create.error instanceof ApiError ? create.error.message : 'Could not record that sale.'}
                  </Alert>
                )}
                {/* PWA: 3 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    { title: 'Mineral & value', content: (
                      <div className="grid grid--2">
                        <Select label="Mineral" name="mineral" required defaultValue="">
                          <option value="" disabled>Select…</option>
                          {minerals.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                        </Select>
                        <Input label="Sale value (ZMW)" name="value" type="number" step="any" min="0" required />
                      </div>
                    ) },
                    { title: 'Quantity', content: (
                      <div className="grid grid--2">
                        <Input label="Quantity" name="quantity" type="number" step="any" min="0" />
                        <Select label="Unit" name="unit" defaultValue="">
                          <option value="">—</option>
                          {units.map((u) => <option key={u} value={u}>{u}</option>)}
                        </Select>
                      </div>
                    ) },
                    { title: 'Buyer & site', content: (
                      <div className="grid grid--2">
                        <Input label="Buyer" name="buyer" />
                        <Select label="Site" name="site_id" defaultValue="">
                          <option value="">—</option>
                          {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </Select>
                      </div>
                    ) },
                    { title: 'Date & pricing', content: (
                      <div className="grid grid--2">
                        <Input label="Date sold" name="sold_at" type="date" defaultValue={new Date().toISOString().slice(0, 10)} />
                        <Input label="Copper price (USD/tonne, if copper)" name="copper_price" type="number" step="any" min="0" />
                      </div>
                    ) },
                    { title: 'Notes', content: (
                      <Input label="Notes" name="notes" />
                    ) },
                  ]}
                  footer={(
                    <div className="row">
                      <Button type="submit" loading={create.isPending} confirm={{ title: "Record this sale?" }}>Record sale</Button>
                      <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
                    </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      {!canViewReports && (
        <Alert tone="info">
          Recorded entries feed your organisation&rsquo;s reports. Ask a leader or an
          admin for reporting access if you need to review past records.
        </Alert>
      )}

      {canViewReports && (
      <Card>
        <CardHeader title="Sales" action={data ? <strong>{formatNumber(data.total, 2)} ZMW total</strong> : undefined} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} w="70%" /></div>
          ) : items.length === 0 ? (
            <EmptyState icon={<CoinIcon size={22} />} title="No sales recorded" body="Recorded mineral sales will appear here." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Date</th><th>Mineral</th><th>Buyer</th><th className="table__num">Quantity</th><th className="table__num">Value (ZMW)</th></tr></thead>
                <tbody>
                  {items.map((s) => (
                    <tr key={s.id}>
                      <td>{formatDate(s.sold_at)}</td>
                      <td style={{ fontWeight: 600 }}>{titleCase(s.mineral)}{isCopper(s.mineral ?? '') && s.copper_price ? ` (@ $${s.copper_price}/t)` : ''}</td>
                      <td>{s.buyer || '—'}</td>
                      <td className="table__num">{s.quantity != null ? `${formatNumber(s.quantity, 2)} ${s.unit || ''}` : '—'}</td>
                      <td className="table__num">{formatNumber(s.value, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      )}
    </>
  )
}
